import { Link } from "react-router-dom";
import { Star, Quote, ArrowUpRight } from "lucide-react";
import { TESTIMONIALS as TIDS } from "@/constants/testIds";
import { TESTIMONIALS } from "@/config/site";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

export default function TestimonialsPage() {
  const avg = (
    TESTIMONIALS.reduce((acc, t) => acc + t.rating, 0) / TESTIMONIALS.length
  ).toFixed(1);

  useSEO({
    title: "Avis clients — 4.9/5 sur Google & bouche-à-oreille",
    description:
      "Découvrez les témoignages de clients de PENOW à Fondettes, Tours et en Indre-et-Loire : rénovation complète, ravalement de façade, installation de climatisation et pompe à chaleur, isolation. Note moyenne 4.9/5.",
    keywords:
      "avis PENOW, avis clients rénovation Fondettes, avis climatisation Tours, témoignage entreprise BTP 37",
    path: "/temoignages",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Prestations PENOW",
      description: "Rénovation générale, climatisation & PAC, façade, toiture, isolation",
      brand: { "@type": "Brand", name: "PENOW" },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: avg,
        reviewCount: TESTIMONIALS.length,
        bestRating: "5",
      },
      review: TESTIMONIALS.map((t) => ({
        "@type": "Review",
        author: { "@type": "Person", name: t.name },
        reviewRating: {
          "@type": "Rating",
          ratingValue: t.rating,
          bestRating: "5",
        },
        reviewBody: t.text,
      })),
    },
  });

  return (
    <div data-testid={TIDS.section}>
      {/* Header */}
      <section className="container-editorial pt-16 md:pt-24 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="eyebrow mb-6">Témoignages clients</div>
            <h1 className="font-display text-5xl md:text-6xl leading-tight text-anthracite">
              Ce que nos clients
              <br />
              <span className="text-terracotta">racontent de nous.</span>
            </h1>
          </div>
          <div className="lg:col-span-4 flex md:justify-end">
            <div className="border border-black/10 bg-white p-6 min-w-[240px]">
              <div className="flex items-center gap-2 text-terracotta">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4" strokeWidth={1.5} fill="currentColor" />
                ))}
              </div>
              <div className="mt-3 font-display text-4xl text-anthracite">{avg}/5</div>
              <div className="text-xs uppercase tracking-[0.22em] text-anthracite/50 mt-1">
                Moyenne sur {TESTIMONIALS.length} avis
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="container-editorial pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t, i) => (
            <Reveal
              key={t.id}
              delay={(i % 6) * 80}
              data-testid={TIDS.card}
              className="relative border border-black/10 bg-white p-8 hover-lift hover:border-copper"
            >
              <Quote className="w-8 h-8 text-terracotta/30 mb-4" strokeWidth={1} />
              <div className="flex items-center gap-1 mb-4 text-terracotta">
                {[...Array(t.rating)].map((_, k) => (
                  <Star key={k} className="w-4 h-4" strokeWidth={1.5} fill="currentColor" />
                ))}
              </div>
              <p className="text-anthracite/80 leading-relaxed">
                "{t.text}"
              </p>
              <div className="mt-6 pt-6 border-t border-black/10">
                <div className="font-medium text-anthracite">{t.name}</div>
                <div className="text-xs uppercase tracking-[0.22em] text-anthracite/50 mt-1">
                  {t.role}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 bg-anthracite text-white p-10 md:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <div className="eyebrow text-terracotta mb-3">Rejoignez-les</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-xl">
              Faites confiance à PENOW pour votre prochain chantier.
            </h2>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-terracotta hover:bg-copper text-white px-6 py-4 tracking-wide transition-colors self-start md:self-auto"
            data-testid="testimonials-cta-quote"
          >
            Demander un devis
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
